/**
 * Cliente HTTP del módulo English para `apps/speak`.
 *
 * Centraliza todas las llamadas a `/api/modules/english/*` que el rediseño V2
 * necesita (lookup de palabra/frase, guardar vocabulario, explicación hablada
 * de correcciones, scoring de pronunciación y TTS del coach). Es deliberadamente
 * delgado y NO importa nada de `apps/client`: Speak no debe acoplarse al cliente
 * viejo (ver Fase 07, §"conectar antes que reimplementar").
 *
 * Todas las requests usan `credentials: 'include'` — `/api` se sirve same-origin
 * vía el proxy de Vite en dev; la cookie httpOnly de sesión viaja sola.
 *
 * El backend responde con envelope `{ success, data }` en los JSON; los
 * endpoints de audio devuelven `audio/mpeg` (correcciones, TTS) o reciben
 * `audio/wav` (pa-practice, say-check).
 */

import { isWordAudio, normalizeLegacyConvTone } from '@orborix/shared-types';
import type {
  A0Stage,
  ConversationPrefs,
  CorrectionLevel,
  EnglishIntensity,
  SpanishVariant,
  SttLanguageMode,
} from '@orborix/shared-types';

import { createTypedApiClient } from '@orborix/auth-client';

const BASE = '/api/modules/english';

const client = createTypedApiClient({
  basePath: BASE,
  errorName: 'EnglishApiError',
  // Ningún fetcher de este wrapper acepta `signal` ⇒ el `AbortError` no es
  // alcanzable. El criterio de `'wrap'` vs `'rethrow'` está en
  // `docs/architecture/09-speak.md`.
  abortError: 'wrap',
});

/**
 * Error con código crudo del backend; el caller decide el copy user-facing.
 * `details` trae el body crudo del error (p. ej. `{ retryAt }` del cooldown
 * del self-set de nivel).
 */
export const EnglishApiError = client.ApiError;
export type EnglishApiError = InstanceType<typeof client.ApiError>;

const jsonFetch = client.get;

/** Endpoints que devuelven audio (`audio/mpeg`) en vez de JSON. POST salvo que
 *  el caller diga otra cosa (`/word-audio/:word` es GET, para que el navegador
 *  pueda cachearlo). */
async function blobFetch(path: string, init: RequestInit = {}): Promise<Blob> {
  const res = await client.request(path, { method: 'POST', ...init });
  await client.ensureOk(res);
  return res.blob();
}

// ─── Lookup de palabra / frase ────────────────────────────────────────────
export interface LookupResult {
  translation: string;
  definition: string | null;
  partOfSpeech: string | null;
  examples: ReadonlyArray<string>;
}

/**
 * Traduce/define una palabra o frase en contexto. El backend cachea palabras
 * comunes sin contexto en memoria, así que los lookups repetidos vuelven <50ms.
 * Reemplaza los diccionarios hardcodeados (`WORD_DICT`/`PHRASE_DICT`) del proto.
 */
export function lookupText(input: {
  text: string;
  context?: string;
  /** Sesión activa — el backend la usa para cobrar el lookup y mostrarlo en el BO. */
  sessionId?: string;
}): Promise<LookupResult> {
  return jsonFetch<LookupResult>('/lookup', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/** Cache en memoria de traducciones de oración (por texto), para no re-cobrar al
 *  togglear la traducción de la misma burbuja del coach. Cap LRU: en una sesión
 *  SPA larga sin reload crecería de forma monótona; el tope evita acumulación. */
const SENTENCE_CACHE_MAX = 500;
const sentenceTranslationCache = new Map<string, string>();

/**
 * Traduce la ORACIÓN entera de una burbuja del coach. A diferencia de
 * `lookupText` (palabra/frase, shape de vocab con cap de 80 chars), pega al
 * `/translate`, que usa el traductor de ORACIONES del backend (el mismo que la
 * traducción global) — así no se trunca y queda consistente con la global.
 */
export async function translateSentence(input: {
  text: string;
  sessionId?: string;
}): Promise<{ translation: string }> {
  const key = input.text.trim();
  const hit = sentenceTranslationCache.get(key);
  if (hit !== undefined) return { translation: hit };
  const res = await jsonFetch<{ translation: string }>('/translate', {
    method: 'POST',
    body: JSON.stringify(input),
  });
  sentenceTranslationCache.set(key, res.translation);
  if (sentenceTranslationCache.size > SENTENCE_CACHE_MAX) {
    const oldest = sentenceTranslationCache.keys().next().value;
    if (oldest !== undefined) sentenceTranslationCache.delete(oldest);
  }
  return res;
}

/**
 * Guarda una palabra/frase en el vocabulario del user. Idempotente: si ya
 * existía devuelve `{ alreadyExisted: true }` sin duplicar.
 */
export function saveVocab(input: {
  word: string;
  translation?: string;
  sessionId?: string;
  sourceMessageIdx?: number;
}): Promise<{ id: string; alreadyExisted: boolean }> {
  return jsonFetch<{ id: string; alreadyExisted: boolean }>('/vocab/save', {
    method: 'POST',
    body: JSON.stringify(input),
  });
}

/**
 * Des-guarda una palabra/frase por TEXTO (el popover no conoce el id de la
 * entrada). El backend resuelve por `text_norm` del user y borra de verdad
 * (fin del toggle mentiroso — 20-H Fase 0.2). Idempotente: si ya no existía
 * devuelve `{ deleted: false }` sin error.
 */
export function deleteVocabByText(input: { text: string }): Promise<{ deleted: boolean }> {
  return jsonFetch<{ deleted: boolean }>('/vocab/by-text', {
    method: 'DELETE',
    body: JSON.stringify(input),
  });
}

// ─── Explicación EXPANDIDA bilingüe (texto, on-demand) ──────────────────────
export type ExplainLangResolved = 'native' | 'target';
/** Niveles de profundización de la explicación expandida (1..MAX_EXPLAIN_LEVEL). */
export const MAX_EXPLAIN_LEVEL = 3;
export interface ExplainCorrectionTextInput {
  said: string;
  fix: string;
  explanation?: string | null;
  /** 'auto' = el backend decide por nivel CEFR. */
  lang: ExplainLangResolved | 'auto';
  /** Nivel de profundización (1..3). Cada "Expandir más" lo incrementa. */
  level?: number;
  /** Explicaciones (en inglés) ya mostradas, para que el backend no repita. */
  previous?: ReadonlyArray<string>;
  sessionId?: string;
}
export interface ExplainCorrectionTextResult {
  /** Explicación en inglés (lo que se reproduce como audio). */
  en: string;
  /** Misma explicación en español (display para niveles bajos). */
  es: string;
  /** Idioma resuelto por el backend (cuando se pidió 'auto'). */
  lang: ExplainLangResolved;
}

/**
 * Reintentos automáticos de la explicación expandida ante un fallo
 * TRANSITORIO, y espera entre uno y otro (crece por intento).
 *
 * Por qué existen: el 2026-09-11 el botón "Expandir explicación" falló dos
 * veces seguidas y anduvo a la tercera. El backend no falló ni una: el único
 * span `anthropic.explain-correction-text` de esa corrección salió OK en
 * 3,1 s. O sea que los dos fallos murieron en el camino navegador↔backend y
 * el user quedó reintentando a mano lo que la app puede reintentar sola.
 *
 * Reintentar sale casi gratis: el backend cachea la explicación por
 * (said, fix, explanation) + nivel en un LRU por proceso, así que si la
 * request original SÍ llegó y lo que se perdió fue la respuesta, el reintento
 * pega en la cache y vuelve en milisegundos sin pagar otro LLM.
 */
const EXPLAIN_RETRIES = 2;
const EXPLAIN_RETRY_DELAY_MS = 500;

/**
 * `true` si el fallo puede desaparecer repitiendo la MISMA request: se cayó el
 * round-trip (`status 0` — red, proxy, gate del preview) o el backend contestó
 * 5xx/408. Un 4xx (body inválido, sin créditos) es determinista: reintentarlo
 * sólo quema tiempo.
 *
 * El 503 también es determinista acá y por eso queda afuera: este endpoint sólo
 * lo usa para "no hay proveedor que atienda" (keys sin configurar, o la cuenta
 * de la plataforma sin saldo con el proveedor de IA). Eso lo destraba un humano,
 * no el segundo viaje.
 */
function isTransientExplainError(err: unknown): boolean {
  const status = (err as { status?: number } | null)?.status;
  if (status === 0 || status === 408) return true;
  return typeof status === 'number' && status >= 500 && status !== 503;
}

/**
 * Motivo legible del fallo, para el CTA de reintentar. Mismo criterio que
 * `describeTurnAudioError` de `sessionsApi`: sin esto, un backend caído y una
 * corrección que el modelo no supo explicar se pintan idénticos y el reporte
 * del user no alcanza para diagnosticar nada.
 */
export function describeExplainError(err: unknown): string {
  const e = err as { status?: number; code?: string } | null;
  const status = e?.status;
  // `provider_unavailable` es el proveedor de IA de la plataforma, no el saldo
  // del user: nunca decir "sin créditos" acá (los suyos están bien).
  if (e?.code === 'provider_unavailable') return 'el servicio no está disponible';
  if (status === 0) return 'no pudimos conectar';
  if (status === 401 || status === 403) return 'la sesión expiró';
  if (status === 402) return 'sin créditos';
  if (status === 503) return 'el servicio no está disponible';
  // 502 = el backend llegó al proveedor y no trajo explicación (rate limit,
  // caída, respuesta ilegible). Ya se reintentó solo; el CTA ofrece el manual.
  if (status === 502) return 'no se pudo generar';
  if (typeof status === 'number' && status > 0) return `error ${status}`;
  return 'no se pudo generar';
}

/**
 * Explicación expandida en texto, en ambos idiomas + el idioma resuelto.
 * Reintenta sola los fallos transitorios (ver {@link EXPLAIN_RETRIES}); los
 * deterministas (4xx) los tira en el primer intento.
 */
export async function explainCorrectionText(
  input: ExplainCorrectionTextInput,
): Promise<ExplainCorrectionTextResult> {
  for (let attempt = 0; ; attempt++) {
    try {
      return await jsonFetch<ExplainCorrectionTextResult>('/explain-correction-text', {
        method: 'POST',
        body: JSON.stringify(input),
      });
    } catch (err) {
      if (attempt >= EXPLAIN_RETRIES || !isTransientExplainError(err)) throw err;
      console.warn(
        `[expand-explain] intento ${attempt + 1} falló (${describeExplainError(err)}) — reintentando`,
        err,
      );
      await new Promise((r) => setTimeout(r, EXPLAIN_RETRY_DELAY_MS * (attempt + 1)));
    }
  }
}

// ─── Estado de correcciones desde el debrief (por id) ───────────────────────
/** Acción del alumno sobre correcciones (`PATCH /corrections/state`). */
export type CorrectionStateAction =
  | 'discard'
  | 'undiscard'
  | 'mark_known'
  | 'unmark_known'
  | 'review_ok'
  | 'review_ok_retry'
  | 'review_again'
  /** «Descartar esta frase» en el repaso (Emmanuel, 19/10): sale sin contar. */
  | 'review_discard';

/**
 * Descartar (👎 con motivo), "ya lo sé" y resultado del repaso, sobre las filas
 * crudas de una fila del debrief (`memberIds`). El backend devuelve 404 si
 * alguna no es del user — el caller revierte su optimista.
 */
export function patchCorrectionState(
  ids: ReadonlyArray<string>,
  action: CorrectionStateAction,
  reason?: Extract<CorrectionFeedbackReason, 'not_said' | 'was_fine'>,
  /**
   * Sólo review_*: el repaso de un foco del Radar (no cuenta para el tope del día), o
   * «Decilas ahora» de la nave (`ship`: las palabras guardadas no se programan).
   */
  via?: 'radar' | 'ship',
): Promise<{ updated: number; nextReviewAt?: string; learned?: boolean }> {
  // `nextReviewAt` (y `learned`) sólo con review_*: cuándo vuelve, del server.
  return jsonFetch<{ updated: number; nextReviewAt?: string; learned?: boolean }>(
    '/corrections/state',
    {
      method: 'PATCH',
      body: JSON.stringify({ ids, action, ...(reason ? { reason } : {}), ...(via ? { via } : {}) }),
    },
  );
}

// ─── Feedback de corrección (👍/👎 — plan 26 §7.4) ──────────────────────────
export type CorrectionFeedbackVote = 'up' | 'down';
export type CorrectionFeedbackReason = 'not_said' | 'was_fine' | 'dont_understand';

export interface CorrectionFeedbackInput {
  sessionId: string;
  /** `null` = la corrección se persistió sin turno (clave natural del índice). */
  turnIndex: number | null;
  said: string;
  fix: string;
  /** `null` borra el voto (toggle off). */
  vote: CorrectionFeedbackVote | null;
  /** Sólo acompaña al 👎 (chips opcionales). */
  reason?: CorrectionFeedbackReason;
}

/**
 * Voto consultivo sobre una corrección. Identificada por clave natural
 * (sessionId + turnIndex + said + fix) — el WS en vivo no transporta el id de
 * fila. El voto nunca suprime correcciones futuras (señal de calibración).
 */
export function sendCorrectionFeedback(
  input: CorrectionFeedbackInput,
): Promise<{ updated: boolean }> {
  return jsonFetch<{ updated: boolean }>('/corrections/feedback', {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
}

// ─── Focos de repaso — curación + "En el radar" (plan 26 §5.1/D9) ───────────

export type ReviewFocusStatus = 'active' | 'resolved' | 'dismissed';
export type ReviewFocusAction = 'resolve' | 'dismiss' | 'restore';

/** Un ejemplo persistido del foco (la evidencia de que es real). */
export interface ReviewFocusExample {
  said: string;
  fix: string;
  /** ISO timestamp — para fecha relativa en el detalle. */
  at: string;
}

/**
 * Destino accionable de un foco, resuelto server-side (plan 27 B2). `null` es
 * honesto: el foco no tiene un capítulo asignado (pronunciation/register/
 * categoría desconocida) — del radar se sale a estudiar sólo si hay a dónde.
 */
export type ReviewFocusRecommendation =
  | {
      kind: 'unit';
      unit: {
        id: string;
        /** The curriculum's title (English); the app shows `titleEs` (`unitTitle`). */
        title: string;
        titleEs?: string;
        cefr: string;
        hasTheory: boolean;
        /** The lesson inside the unit, when the focus's skill lives in one (W7). */
        subUnitId?: string;
        /** The skill that routed the focus here: what the CTA names (`practiceCta`). */
        skill?: { id: string; label: { es: string; en: string } };
      };
    }
  | { kind: 'practice' }
  | null;

/** DTO de `GET/PATCH /review-focus` (focusToDto del backend). */
export interface ReviewFocusItem {
  id: string;
  pattern: string;
  /**
   * Label corto display-facing (≤60 chars, 2ª persona, es-AR) generado por el
   * distiller al crear el foco (R3). `null` en focos anteriores al label — el
   * front cae a `pattern` (que queda como evidencia interna/técnica).
   */
  label: string | null;
  category: string | null;
  /**
   * Fenómeno de la taxonomía CONTROLADA con su label es-AR resuelto
   * server-side (error-taxonomy.ts). `null` = foco legacy sin backfill o
   * fuera de la lista ('other') — el front cae al mapeo legacy de category.
   */
  taxonomy: { id: string; label: string } | null;
  /**
   * The catalog skill the focus belongs to (`label` es-AR), resolved
   * server-side only when it can be named with confidence; `null` otherwise.
   */
  skill: { id: string; label: string } | null;
  dimension: string | null;
  severity: string;
  hitSessions: number;
  cleanStreak: number;
  streakNeed: number;
  status: ReviewFocusStatus;
  lastOutcome: 'correct_use' | 'error' | 'avoided' | null;
  firstSeenAt: string;
  lastSeenAt: string;
  resolvedAt: string | null;
  resolvedBy: 'system' | 'user' | null;
  dismissedReason: CorrectionFeedbackReason | null;
  /** Hasta 5 ejemplos `{said, fix, at}` — del más viejo al más nuevo. */
  examples: ReviewFocusExample[];
  /** Destino accionable (unit / practice / null). */
  recommendation: ReviewFocusRecommendation;
}

export interface ReviewFocusList {
  active: ReviewFocusItem[];
  resolved: ReviewFocusItem[];
  dismissed: ReviewFocusItem[];
}

/** Lista completa de focos agrupada por estado (los archived no viajan). */
export function getReviewFoci(): Promise<ReviewFocusList> {
  return jsonFetch<ReviewFocusList>('/review-focus');
}

/**
 * Curación de un foco (§5.1): `resolve` = "ya lo superé" (resolved_by user),
 * `dismiss` = "no es un error mío" (sale de todo + NO-recrear en el merge del
 * distiller), `restore` = deshacer un descarte. 409 si el cliente tiene estado
 * viejo (transición inválida).
 */
export function patchReviewFocus(
  focusId: string,
  action: ReviewFocusAction,
  reason?: CorrectionFeedbackReason,
): Promise<{ focus: ReviewFocusItem }> {
  return jsonFetch<{ focus: ReviewFocusItem }>(`/review-focus/${focusId}`, {
    method: 'PATCH',
    body: JSON.stringify({ action, ...(reason ? { reason } : {}) }),
  });
}

// ─── Scoring de pronunciación (drill) ───────────────────────────────────────
export interface PaPracticeResult {
  /** Accuracy de la PRIMERA palabra evaluada (0-100). Para targets de 1 palabra = el score. */
  accuracy: number;
  /**
   * Score del target COMPLETO (0-100). Para FRASES (repeat-after del A0, p. ej.
   * "good morning") es el benchmark correcto; `accuracy` sólo puntúa la 1ª palabra.
   */
  overall?: number;
  /** errorType del provider ('None' si la pronunciación está OK). */
  errorType: string;
  /** Fonemas individuales evaluados. */
  phonemes: ReadonlyArray<{ ipa: string; accuracy: number }>;
}

/**
 * One-shot pronunciation assessment con `referenceText = word`. El audio va
 * como multipart/form-data (WAV PCM16 mono 16kHz — ver `micWavCapture`).
 * Sólo para el drill del «Análisis fonético» (web, opt-in): el backend responde
 * 403 `pa_not_enabled` si el alumno no tiene la pref `phonetic` prendida.
 * Devuelve JSON (envelope `{ success, data }`), no audio.
 */
export function practicePronunciation(word: string, audio: Blob): Promise<PaPracticeResult> {
  const fd = new FormData();
  fd.append('word', word);
  fd.append('audio', audio, 'practice.wav');
  // FormData setea su propio content-type (con boundary): no lo forzamos.
  return jsonFetch<PaPracticeResult>('/pa-practice', { method: 'POST', body: fd });
}

// ─── Decila: ¿dijo la palabra? (STT, no PA) ──────────────────────────────────
export interface SayCheckResult {
  /** Lo oído se reconoce como la palabra (inteligibilidad, no nativeness). */
  pass: boolean;
  /** Lo que oyó el STT, normalizado. Vacío = no se oyó nada. */
  heard: string;
}

/**
 * Decila (Biblioteca): transcribe la toma con Scribe y la compara contra la
 * palabra con la misma vara que el repeat-check del A0 (`POST /say-check`).
 * Sin evaluación de pronunciación: el PA es beta y sólo vive en el «Análisis
 * fonético» web, opt-in. El audio va como multipart (WAV PCM16 mono 16 kHz).
 */
export function checkSaidWord(word: string, audio: Blob): Promise<SayCheckResult> {
  const fd = new FormData();
  fd.append('word', word);
  fd.append('audio', audio, 'say.wav');
  return jsonFetch<SayCheckResult>('/say-check', { method: 'POST', body: fd });
}

// ─── Conversation prefs (UI per-usuario, persistidas en la DB) ──────────────
/**
 * Lee las prefs de conversación del user (modo I/O, mic, intensidad, velocidad,
 * fonética, traducción, corrección, densidad). El backend siempre devuelve el
 * objeto completo (mergeado sobre defaults), así que nunca es parcial.
 */
export async function getConversationPrefs(): Promise<ConversationPrefs> {
  const prefs = await jsonFetch<ConversationPrefs>('/conversation-prefs');
  // Borde de lectura del cliente: un tono retirado el 2026-10-04 (respuesta
  // cacheada, backend sin desplegar) se migra a `warm` — migración conocida,
  // no un default. Un tono que no es vigente ni retirado es un bug: tira.
  const tone = normalizeLegacyConvTone(prefs.tone);
  if (tone === null)
    throw new Error(`conversation-prefs: tono desconocido (${String(prefs.tone)})`);
  return { ...prefs, tone };
}

/**
 * Persiste el objeto COMPLETO de prefs (last-write-wins por dispositivo). El
 * controller siempre tiene el `ConvSettings` entero en memoria, así que no hay
 * merge parcial. NO incluye volumen TTS (device-local en localStorage).
 */
export function saveConversationPrefs(prefs: ConversationPrefs): Promise<ConversationPrefs> {
  return jsonFetch<ConversationPrefs>('/conversation-prefs', {
    method: 'PUT',
    body: JSON.stringify(prefs),
  });
}

/**
 * TTS del coach para una frase puntual (audio/mpeg). `speed` opcional
 * (0.7 = lento).
 *
 * **Una PALABRA suelta no va por `POST /speak` sino por `GET
 * /word-audio/:word`** (2026-09-11). No es una diferencia de contenido —el
 * audio es el mismo y el backend lo decide igual, mirando el texto— sino de
 * transporte: ningún navegador cachea la respuesta a un POST, así que tocar
 * diez veces la misma palabra eran diez viajes a la red. Por GET el segundo tap
 * sale del disco del dispositivo y no llega ni al backend.
 *
 * Se aplica acá y no en cada call site para que lo hereden los seis botones
 * "Escuchar" que ya existen (karaoke de /listen, popover de la conversación,
 * Biblioteca, drill, ficha de error, A0) sin tocarlos uno por uno.
 *
 * Con `speed` se queda en el POST: el catálogo guardado se indexa por (palabra,
 * voz) y nada más, así que una versión lenta no puede entrar ahí.
 */
export function speakText(
  text: string,
  opts: { sessionId?: string; speed?: number } = {},
): Promise<Blob> {
  if (isWordAudio(text) && opts.speed === undefined) return speakWord(text);
  return blobFetch('/speak', {
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      text,
      ...(opts.sessionId ? { sessionId: opts.sessionId } : {}),
      ...(typeof opts.speed === 'number' ? { speed: opts.speed } : {}),
    }),
  });
}

/**
 * Audio de UNA palabra por la URL cacheable. La voz la resuelve el backend (la
 * de diccionario, una sola para todos) — no viaja en la URL a propósito: si la
 * eligiera el cliente, el catálogo guardado se fragmentaría por voz. El cambio
 * de voz se propaga igual, por `ETag`.
 *
 * `blobFetch` sin `cache: 'no-store'`: el sentido de todo esto es que el fetch
 * pase por la cache HTTP del navegador.
 */
export function speakWord(word: string): Promise<Blob> {
  return blobFetch(`/word-audio/${encodeURIComponent(word.trim())}`, { method: 'GET' });
}

/**
 * Copy para el usuario cuando falla el audio del botón "Escuchar". El único
 * caso con mensaje propio es el freno por abuso (429): el audio de palabra no
 * se le cobra al user, así que el tope es de 30 por minuto y el botón tiene que
 * decir por qué dejó de responder en vez de quedarse mudo.
 */
export function describeSpeakError(err: unknown): string {
  if (err instanceof EnglishApiError && err.status === 429) {
    return 'Pediste muchos audios seguidos. Probá de nuevo en un minuto.';
  }
  return 'No pudimos reproducir el audio.';
}

/** Audio del coach + el tiempo (ms) en que suena cada carácter del texto. */
export interface SpokenAudio {
  blob: Blob;
  /** `startsMs[i]` = ms desde el inicio del audio hasta el carácter `i`. */
  startsMs: ReadonlyArray<number>;
}

interface TimestampedSpeakResponse {
  audioBase64: string;
  contentType: string;
  alignment: { startsMs: number[] };
}

/**
 * Igual que {@link speakText} pero pidiendo el alignment por carácter. La
 * respuesta viaja como JSON (audio en base64 + tiempos) porque el karaoke
 * necesita las dos cosas juntas y garantizadas del MISMO render del TTS:
 * timings de una síntesis distinta a la que suena desincronizan el resaltado.
 *
 * Se usa para el replay de burbujas del coach cuando no hay audio grabado del
 * turno (sin `RECORD_SESSION_AUDIO` no hay qué re-reproducir, así que se
 * re-sintetiza — y esta variante hace que igual haya karaoke).
 */
export async function speakTextWithAlignment(
  text: string,
  /**
   * `use: 'review'`: la voz del coach del repaso de correcciones; el server la
   * guarda para siempre y los repasos siguientes no la generan ni la cobran.
   */
  opts: { sessionId?: string; speed?: number; use?: 'review' } = {},
): Promise<SpokenAudio> {
  const res = await client.send<TimestampedSpeakResponse>('/speak', {
    method: 'POST',
    body: JSON.stringify({
      text,
      timestamps: true,
      ...(opts.sessionId ? { sessionId: opts.sessionId } : {}),
      ...(typeof opts.speed === 'number' ? { speed: opts.speed } : {}),
      ...(opts.use ? { use: opts.use } : {}),
    }),
  });
  return { blob: base64ToBlob(res.audioBase64, res.contentType), startsMs: res.alignment.startsMs };
}

/**
 * Deja guardada la voz de una línea del repaso (la genera si falta) sin bajarla: el server
 * responde 204 (card #595). Para que la primera línea del repaso suene apenas se abre.
 */
export async function prepareReviewVoice(text: string): Promise<void> {
  const res = await client.request('/speak', {
    method: 'POST',
    body: JSON.stringify({ text, timestamps: true, use: 'review', prepare: true }),
  });
  await client.ensureOk(res);
}

function base64ToBlob(base64: string, contentType: string): Blob {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: contentType });
}

// ─── Nivelación: estado, triage, intensidad e historial (Fases C/D/E) ────────
// Backend en `apps/backend/src/routes/english/onboarding.ts`. La intensidad
// EFECTIVA (cómo habla el coach) es `effectiveIntensity` SIEMPRE — la
// `recommendedIntensity` es sólo guía a mostrar. El triage puede setear una
// efectiva por debajo de la recomendada a propósito (A1-cero → `starter` aunque
// la recomendada de A1 sea `cozy`). Ver docs/speak-plan/10.

// PROFICIENCIA del usuario (A0..C2): A0 = pre-A1 / principiante absoluto (plan
// 31). El CONTENIDO (dims, libros /listen, vocab) sigue siendo A1..C2 — ver
// `LevelMap` abajo (las dims nunca son A0).
export type CefrLevel = 'A0' | 'A1' | 'A2' | 'B1' | 'B2' | 'C1' | 'C2';
// Las 3 dimensiones medibles por texto (17.E — fluency/pronunciation eliminadas).
export type LevelDimension = 'grammar' | 'vocabulary' | 'functional';
export type LevelMap = Record<LevelDimension, CefrLevel>;
/** De dónde vino un punto del historial del nivel oficial. */
export type LevelSource =
  | 'triage'
  | 'quiz'
  | 'exam'
  | 'manual'
  | 'auto'
  | 'self_set'
  | 'graduation_exam'
  /** A0 → A1 by evidence: frontier crossed + English sustained (plan 48 phase 3). */
  | 'graduation_evidence';
export type OnboardingPath = 'exam' | 'manual' | 'later' | 'triage' | 'quiz';
/** Opción del triage autoreportado (1 pregunta, español). */
export type TriageLevel = 'zero' | 'words' | 'basic' | 'conversational' | 'fluent';

export interface EnglishState {
  onboarded: boolean;
  path: OnboardingPath | null;
  /**
   * Rama beginner que arrancó el quiz de placement pero no lo completó (plan 28).
   * El gate de Home la sigue mandando al onboarding; el orquestador la deposita
   * directo en el quiz (sin repetir el triage).
   */
  pendingQuiz: boolean;
  calibrationConfirmed: boolean;
  levels: LevelMap;
  pronunciationAssessment: boolean;
  paAccuracyThreshold: number;
  /**
   * Nivel CEFR global oficial (el diagnóstico). Puede ser `A0` (pre-A1).
   *
   * `null` = todavía SIN diagnóstico (`onboarded === false`): el user no pasó
   * por triage/quiz/examen. NO es lo mismo que `'A0'`, que es un veredicto del
   * quiz beginner y manda al user a otra app entera (tabs propias + motor
   * Leitner). Tratar el null como A0 metía a cualquier recién registrado en la
   * experiencia A0 antes de evaluarlo.
   */
  cefrGlobal: CefrLevel | null;
  /**
   * Overlay de nivel explorable (plan 38). `null` = sin overlay. Cuando está
   * activo, el usuario está "explorando" otro nivel temporalmente: toda la
   * experiencia adaptativa (coach, correcciones, contenido, STT, velocidad)
   * corre en `level`, pero `cefrGlobal` sigue siendo el REAL. El front muestra
   * el badge "Explorando {level} · tu nivel es {cefrGlobal}" y pausa las
   * features complejas (examen, Radar, festejos) mientras esté activo.
   */
  levelOverlay: LevelOverlay | null;
  /**
   * Etapa de andamiaje A0 (`full`/`medium`/`light`) — cuánta muleta en español
   * presta el coach. `null` salvo cuando `cefrGlobal==='A0'` (invariante DB del
   * plan 31 F3). Lo consume la experiencia A0 (front separado, plan 32).
   */
  a0Stage: A0Stage | null;
  /** Intensidad real con la que habla el coach (= cómo se debe abrir la sesión). */
  effectiveIntensity: EnglishIntensity;
  /** Intensidad recomendada para el nivel — sólo guía visual. */
  recommendedIntensity: EnglishIntensity;
  /**
   * Mapa CEFR→intensidad recomendada, completo. Para pantallas que muestran la
   * recomendación de un nivel que TODAVÍA no es `cefrGlobal` — el reveal del
   * examen (la estimación se propaga recién en `/calibration/confirm`) y los
   * botones "− bajar / subir +". Se indexa, no se redefine: el mapeo vive en el
   * backend (ver el header de `levelData.ts`).
   */
  recommendedIntensityByCefr: Record<CefrLevel, EnglishIntensity>;
  /** Toggle global: el sistema ajusta nivel/intensidad solo (true) o el user manda (false). */
  levelAuto: boolean;
  /**
   * Sensibilidad de correcciones EFECTIVA (filtra qué severidades se muestran).
   * Resuelta server-side: auto ? recomendada-por-CEFR : elección manual.
   */
  effectiveCorrectionLevel: CorrectionLevel;
  /** Sensibilidad recomendada para el nivel CEFR — sólo guía visual. */
  recommendedCorrectionLevel: CorrectionLevel;
  /** Toggle: la sensibilidad sigue la recomendación por CEFR (true) o es manual (false). */
  correctionAuto: boolean;
  /**
   * Idioma del STT recomendado por nivel EFECTIVO (`auto`/`en`) — preset por
   * nivel (plan 38 F1). El cliente deriva el efectivo como
   * `pref.sttLanguage ?? recommendedSttLanguage` sin duplicar el mapa CEFR→valor.
   */
  recommendedSttLanguage: SttLanguageMode;
  /**
   * Velocidad de voz recomendada por nivel EFECTIVO (0.7–1.2) — preset por
   * nivel (plan 38 F1). El cliente deriva la efectiva como
   * `pref.speed ?? recommendedSpeed` (pref null = auto por nivel).
   */
  recommendedSpeed: number;
  /**
   * "Español del profe" SIN pref (país de origen > rioplatense legacy). La UI
   * preselecciona `pref.spanishVariant ?? recommendedSpanishVariant`.
   */
  recommendedSpanishVariant: SpanishVariant;
  /** El que usa el coach hoy (pref > país > legacy), resuelto server-side. */
  effectiveSpanishVariant: SpanishVariant;
}

/**
 * `EnglishState` de un user YA diagnosticado (pasó triage/quiz/examen): el
 * `cefrGlobal` está garantizado. Es lo que consumen las pantallas que viven
 * detrás del gate de onboarding y que necesitan un nivel para renderizar
 * (anillo del header, badge de exploración, sheet de nivel).
 */
export type DiagnosedEnglishState = EnglishState & { cefrGlobal: CefrLevel };

/**
 * ¿El user ya tiene nivel diagnosticado? Type-guard: pasado el gate, TS sabe
 * que `cefrGlobal` existe y las pantallas dejan de necesitar un `?? 'B1'` que
 * inventaría un nivel.
 *
 * Chequea las DOS caras del mismo estado server-side (`onboarded` y el nivel):
 * el backend las mantiene en tándem —todo asignador de nivel llama a
 * `markOnboarded`— pero un consumidor que asuma una y use la otra es
 * exactamente el bug que este contrato viene a matar.
 */
export function isDiagnosed(state: EnglishState): state is DiagnosedEnglishState {
  return state.onboarded && state.cefrGlobal !== null;
}

/** Nivel explorable (plan 38): A0 no es explorable (es otra app entera). */
export type ExploreCefrLevel = Exclude<CefrLevel, 'A0'>;

/** Overlay de nivel activo (plan 38). `startedAt` ISO8601 o null. */
export interface LevelOverlay {
  level: ExploreCefrLevel;
  startedAt: string | null;
}

export interface TriageResult {
  cefrGlobal: CefrLevel;
  intensity: EnglishIntensity;
}

export interface LevelHistoryEntry {
  cefrGlobal: CefrLevel;
  intensity: EnglishIntensity;
  source: LevelSource;
  rationale: string | null;
  /** ISO8601. */
  at: string;
}

export interface CalibrationEstimate {
  dimension: LevelDimension;
  level: CefrLevel;
  rationale?: string;
  confidence?: number;
}

export interface CalibrationResult {
  /** false mientras el distiller todavía no escribió las dimensiones. */
  ready: boolean;
  levels: LevelMap | null;
  /**
   * ISO de la calibración más reciente (máx de las dims), o null. Permite al
   * reveal distinguir una estimación fresca del examen recién hecho de las
   * filas viejas (el distill corre async al cerrar la sesión).
   */
  calibratedAt: string | null;
  estimates: ReadonlyArray<CalibrationEstimate>;
}

/** Estado del módulo: onboarding + nivel global + intensidades + toggle auto. */
export function getEnglishState(): Promise<EnglishState> {
  return jsonFetch<EnglishState>('/state');
}

// ─── Coach-marks de onboarding contextual de /conversation (mig 0255) ────────
/** Ids de los coach-marks que el user YA vio (para no re-mostrarlos). */
export async function getCoachmarksSeen(): Promise<string[]> {
  const res = await jsonFetch<{ seen: string[] }>('/coachmarks');
  return res.seen;
}

/** Marca un coach-mark como visto (persistencia por-usuario, no localStorage). */
export async function markCoachmarkSeen(markId: string): Promise<void> {
  await jsonFetch<{ success: boolean }>('/coachmarks/seen', {
    method: 'POST',
    body: JSON.stringify({ markId }),
  });
}

// ─── «Armá tu nave» (encargo 117, mig 0433) ─────────────────────────────────
export type ShipStatus = 'pending' | 'active' | 'skipped' | 'launched';
export type ShipPieceIdApi =
  | 'cabina'
  | 'antena'
  | 'rumbo'
  | 'tren'
  | 'bodega'
  | 'motores'
  | 'radar';
/** El recorrido de las 7 piezas: estado, piezas hechas (calculadas de la actividad real) y avisos. */
export interface ShipState {
  status: ShipStatus;
  /** Cada pieza: si está hecha y cuánto lleva su actividad más reciente (`have` de `need`). */
  pieces: {
    id: ShipPieceIdApi;
    done: boolean;
    have: number;
    need: number;
    unit: 'seconds' | 'lesson' | 'objectives' | 'words' | 'rounds' | 'visit';
    /**
     * Sólo la Cabina: cosas dichas en la charla. `helped`: las mandadas con «Inspirar», que cuentan
     * sólo en A0 (`inspireCounts`); desde A1 cuenta sólo lo dicho en voz alta (card #603).
     */
    said?: { have: number; need: number; helped: number; inspireCounts: boolean };
  }[];
  /** Hechas que el Inicio todavía no animó (entran en espiral). */
  fresh: ShipPieceIdApi[];
  /** Mostrar «Tu nave está en viaje» en esta entrada al Inicio. */
  travel: boolean;
  launchedAt: string | null;
}
export type ShipAction = 'start' | 'restart' | 'skip' | 'radar' | 'launch' | 'travel-seen';

export function getShip(): Promise<ShipState> {
  return jsonFetch<ShipState>('/ship');
}
export function postShipAction(action: ShipAction): Promise<ShipState> {
  return jsonFetch<ShipState>(`/ship/${action}`, { method: 'POST' });
}
/** La Antena: segundos escuchados de verdad (con el audio sonando) desde el último reporte. */
/** `chapterDone`: terminó de verdad un capítulo más corto que dos minutos (completa la Antena). */
export function postShipListen(seconds: number, chapterDone = false): Promise<ShipState> {
  return jsonFetch<ShipState>('/ship/listen', {
    method: 'POST',
    body: JSON.stringify(chapterDone ? { seconds, chapterDone } : { seconds }),
  });
}
/** El Inicio ya animó estas piezas (no vuelven a entrar en espiral). */
export function postShipSeen(pieces: readonly ShipPieceIdApi[]): Promise<ShipState> {
  return jsonFetch<ShipState>('/ship/seen', {
    method: 'POST',
    body: JSON.stringify({ pieces }),
  });
}

/** Setea el nivel inicial (CEFR + intensidad) por autoreporte; marca onboarded. */
export function postTriage(level: TriageLevel): Promise<TriageResult> {
  return jsonFetch<TriageResult>('/triage', {
    method: 'POST',
    body: JSON.stringify({ level }),
  });
}

// ─── Placement quiz (rama beginner, plan 28) ─────────────────────────────
export type PlacementOutcome = 'full' | 'medium' | 'light' | 'ceiling';
export type PlacementBlockId = 'B1' | 'B2' | 'B3';

export interface PlacementOption {
  id: string;
  text: string;
}

/** Un ítem del quiz. `choice` (vocab/funcional) usa `stimulus`+`audio`; `cloze` usa `sentence`. */
export interface PlacementItem {
  id: string;
  block: PlacementBlockId;
  cefr: CefrLevel;
  kind: 'choice' | 'cloze';
  prompt: string;
  options: ReadonlyArray<PlacementOption>;
  /** Respuesta correcta: el front la usa para feedback + corte de escalera; el server re-puntúa. */
  correctOptionId: string;
  /** choice: texto en inglés que se evalúa (palabra / pregunta). */
  stimulus?: string;
  /** choice (B1): nombre del clip de audio en /placement/. */
  audio?: string;
  /** cloze: oración con el hueco marcado por `___`. */
  sentence?: string;
}

export interface PlacementBlock {
  id: PlacementBlockId;
  cefr: CefrLevel;
  /** Aciertos mínimos para avanzar al bloque siguiente. */
  passThreshold: number;
  items: ReadonlyArray<PlacementItem>;
}

export interface PlacementBank {
  bankVersion: string;
  blocks: ReadonlyArray<PlacementBlock>;
}

export interface PlacementAnswer {
  itemId: string;
  optionId: string;
}

export interface PlacementSubmitResult {
  outcome: PlacementOutcome;
  cefrGlobal: CefrLevel;
  intensity: EnglishIntensity;
  /** true → el front ofrece el examen conversacional opt-in (techo). */
  ceiling: boolean;
}

/**
 * Marca la rama beginner ("Recién empiezo"): NO asigna nivel, deja `pendingQuiz`.
 * El quiz de placement decide el andamiaje y completa el onboarding.
 */
export function postTriageBeginner(): Promise<{ pendingQuiz: boolean }> {
  return jsonFetch<{ pendingQuiz: boolean }>('/triage', {
    method: 'POST',
    body: JSON.stringify({ level: 'beginner' }),
  });
}

/** Banco vigente del quiz de placement (ítems + opciones). */
export function getPlacementQuiz(): Promise<PlacementBank> {
  return jsonFetch<PlacementBank>('/placement-quiz');
}

/** Envía las respuestas crudas; el backend puntúa y devuelve la salida de la escalera. */
export function postPlacementQuiz(
  answers: ReadonlyArray<PlacementAnswer>,
): Promise<PlacementSubmitResult> {
  return jsonFetch<PlacementSubmitResult>('/placement-quiz', {
    method: 'POST',
    body: JSON.stringify({ answers }),
  });
}

/**
 * Ajusta la intensidad manual y/o el toggle auto/manual. Al fijar intensidad a
 * mano mandamos `levelAuto:false` junto (el backend no lo infiere): elegir una
 * intensidad ES pasarse a manual.
 */
/**
 * Persiste la sensibilidad de pronunciación (`paAccuracyThreshold`, 30-90).
 * Presets de UI: 40 Permisivo / 60 Equilibrado / 80 Estricto. Columna tipada
 * en `language_user_state` (no parte de `conversationPrefs`).
 */
export function setPaThreshold(accuracyThreshold: number): Promise<void> {
  return jsonFetch<unknown>('/pa-threshold', {
    method: 'POST',
    body: JSON.stringify({ accuracyThreshold }),
  }).then(() => undefined);
}

export function postLevel(patch: {
  intensity?: EnglishIntensity;
  levelAuto?: boolean;
}): Promise<void> {
  return jsonFetch<unknown>('/level', {
    method: 'POST',
    body: JSON.stringify(patch),
  }).then(() => undefined);
}

// ─── Overlay de nivel explorable (plan 38 F3) ───────────────────────────────
// Activar/desactivar la exploración temporal de otro nivel. El backend (F2)
// nunca toca `cefr_level`/historial: es una capa de resolución. En error tira
// `EnglishApiError` con `.code` del backend (a0_cannot_explore / same_as_real_level
// / not_onboarded / in_graduate_welcome) → el caller decide el copy.

/** Activa el overlay: explorar `level` (A1..C2). */
export function activateLevelExplore(level: ExploreCefrLevel): Promise<void> {
  return jsonFetch<unknown>('/level/explore', {
    method: 'POST',
    body: JSON.stringify({ level }),
  }).then(() => undefined);
}

/** Desactiva el overlay → vuelve al nivel real. Idempotente. */
export function deactivateLevelExplore(): Promise<void> {
  return jsonFetch<unknown>('/level/explore', { method: 'DELETE' }).then(() => undefined);
}

/**
 * Promoción self-set (plan 38 F4): fija `level` como el nivel REAL. En error
 * tira `EnglishApiError` con `.code` ('cooldown' → `.details.retryAt`,
 * 'a0_cannot_self_set', 'not_onboarded'). `applyRecommended` (opt-in) además
 * alinea intensidad + toggle auto al nivel nuevo.
 */
export function selfSetLevel(level: ExploreCefrLevel, applyRecommended = false): Promise<void> {
  return jsonFetch<unknown>('/level/self-set', {
    method: 'POST',
    body: JSON.stringify({ level, ...(applyRecommended ? { applyRecommended: true } : {}) }),
  }).then(() => undefined);
}

/** Historial del nivel oficial (DESC), para el gráfico de evolución. */
export function getLevelHistory(limit = 100): Promise<ReadonlyArray<LevelHistoryEntry>> {
  return jsonFetch<ReadonlyArray<LevelHistoryEntry>>(`/level/history?limit=${limit}`);
}

/** Resultado del examen oral (calibration distiller). `ready:false` → todavía estimando. */
export function getCalibrationResult(): Promise<CalibrationResult> {
  return jsonFetch<CalibrationResult>('/calibration/result');
}

// ─── Progreso "El Camino": agregación día-a-día + totales ───────────────────
// Backend en `apps/backend/src/routes/english/progress.ts`. La agrupación por
// día es en la zona horaria del navegador (un día termina a medianoche LOCAL).

/** Correcciones de un día: total + cuántas fueron estructurales/graves. */
export interface DayCorrections {
  total: number;
  structural: number;
}

/** Un día CON actividad en el camino (las fuentes vacías quedan en 0/null). */
export interface DayProgress {
  /** Etiqueta del día local, `YYYY-MM-DD`. */
  date: string;
  /** Minutos de charla = tiempo ACTIVO (`voice_sessions.active_seconds`). */
  minutes: number;
  /** De esos minutos, cuántos habló el alumno en voz alta. */
  spokenMinutes: number;
  /** Cantidad de sesiones/charlas ese día. */
  sessions: number;
  /** Palabras nuevas habladas por primera vez ese día. */
  newWords: number;
  /** Capítulos de audiolibro escuchados ese día. */
  chapters: number;
  /** Correcciones recibidas, o null si no hubo. */
  corr: DayCorrections | null;
  /** Título de una unidad del programa completada ese día, o null. */
  unit: string | null;
  /** Pronunciación promedio (0-100) si hubo evaluación, o null. */
  pronun: number | null;
  /** CEFR alcanzado si ese día subió de nivel, o null. */
  levelUp: CefrLevel | null;
}

export interface DailyProgress {
  tz: string;
  from: string;
  to: string;
  /** Primer día con actividad de toda la historia (para el cap "Acá empezó todo"). */
  firstActiveDate: string | null;
  /** Días con actividad, orden Hoy→pasado. */
  days: ReadonlyArray<DayProgress>;
  /** Días (`YYYY-MM-DD`) con un repaso hecho: cuentan para la racha (card #604). */
  reviewDays: ReadonlyArray<string>;
}

/** Acumulados de toda la trayectoria + racha (pestaña Totales). */
export interface ProgressTotals {
  words: number;
  /** Minutos de charla de toda la trayectoria (tiempo activo). */
  minutes: number;
  /** De esos minutos, cuántos habló el alumno en voz alta. */
  spokenMinutes: number;
  chapters: number;
  sessions: number;
  currentStreak: number;
  bestStreak: number;
  activeDays: number;
}

/** Zona horaria IANA del navegador (ej. "America/Argentina/Buenos_Aires"). */
export function browserTimeZone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
}

/** Camino día-a-día. `tz` = zona del browser; `days` = ventana hacia atrás. */
export function getProgressDaily(tz: string, days = 120): Promise<DailyProgress> {
  return jsonFetch<DailyProgress>(`/progress/daily?tz=${encodeURIComponent(tz)}&days=${days}`);
}

/** Totales acumulados + racha. */
export function getProgressTotals(tz: string): Promise<ProgressTotals> {
  return jsonFetch<ProgressTotals>(`/progress/totals?tz=${encodeURIComponent(tz)}`);
}

/** Cierra el examen: confirma (con overrides opcionales por dimensión) y marca onboarded. */
export function postCalibrationConfirm(overrides: Partial<LevelMap>): Promise<void> {
  return jsonFetch<unknown>('/calibration/confirm', {
    method: 'POST',
    body: JSON.stringify({ overrides }),
  }).then(() => undefined);
}

// ─── "Tu escalón" (vista del escalón de vocabulario, stream C) ────────────

export interface LadderStep {
  id: string;
  size: number;
  status: 'done' | 'current' | 'locked';
}

export interface LadderWord {
  w: string;
  /** El alumno ya la conoce (fila en user_word_usage — mismo criterio que Biblioteca). */
  known: boolean;
  /** La produjo (charla o práctica). */
  said: boolean;
}

export interface LadderFamily {
  /** Id estable del backend; el título ES vive en la UI. */
  id: string;
  words: ReadonlyArray<LadderWord>;
}

export interface VocabLadder {
  kind: 'starter' | 'cozy';
  /** Sub-nivel actual (1-3); null = escalón completo (sin señal). */
  subLevel: 1 | 2 | 3 | null;
  /**
   * Qué señal lo determina: la frontera «Primeros pasos» del A0 (starter,
   * plan 48 F2 — antes el Leitner del motor A0) o E[ord] (cozy).
   */
  signal: 'frontier' | 'eord' | null;
  steps: ReadonlyArray<LadderStep>;
  families: ReadonlyArray<LadderFamily>;
  knownCount: number;
  saidCount: number;
  totalWords: number;
  nextUnlock: { stepId: string; newWords: number; sample: ReadonlyArray<string> } | null;
}

export interface LadderTargetWord {
  word: string;
  translationEs: string;
}

export interface VocabLadderView {
  intensity: EnglishIntensity;
  cefrLevel: CefrLevel;
  /** null = la intensidad del user no tiene lista (natural+). */
  ladder: VocabLadder | null;
  /**
   * Sólo natural+ con vocabulario progresivo ON: las palabras objetivo del
   * nivel CEFR siguiente (targets-only) — la progresión visible cuando no
   * hay escalón de lista. Ausente/null = sin targets (toggle OFF o sin stock).
   */
  targets?: { cefr: string; words: ReadonlyArray<LadderTargetWord> } | null;
  /**
   * Sólo un alumno A0 (plan 48 fase 3): cuánto le falta para pasar a A1 —
   * frontera «Primeros pasos» cruzada Y charlas seguidas con inglés. La misma
   * regla que aplica el distill. Ausente/null = no es A0.
   */
  a0Graduation?: A0GraduationProgress | null;
}

/** Progreso de la graduación A0 → A1 por evidencia (`evaluateA0Graduation` del backend). */
export interface A0GraduationProgress {
  pass: boolean;
  frontier: { known: number; total: number; pct: number; required: number; met: boolean };
  english: { sustained: number; required: number; minShare: number; met: boolean };
  missing: ReadonlyArray<'frontier' | 'english'>;
}

/** El escalón de vocabulario del alumno (lista del coach a su nivel + estados). */
export function getVocabLadder(): Promise<VocabLadderView> {
  return jsonFetch<VocabLadderView>('/vocab/ladder');
}

// ─── «Tu rutina» (mig 0426, card #547) ───────────────────────────────────────
// Backend en `apps/backend/src/routes/english/routine.ts`.

/** La rutina del alumno. `weekdays`: lunes = 0 … domingo = 6. */
export interface Routine {
  /** Minutos por día: 5 · 10 · 15 · 20. */
  minutes: number;
  weekdays: number[];
  /** Hora local del aviso (0-23) o null = sin aviso. Los avisos todavía no salen. */
  reminderHour: number | null;
  committedAt: string;
}

export type RoutineInput = Pick<Routine, 'minutes' | 'weekdays' | 'reminderHour'>;

/** `null` = no eligió rutina («Ahora no»). */
export async function getRoutine(): Promise<Routine | null> {
  const res = await jsonFetch<{ routine: Routine | null }>('/routine');
  return res.routine;
}

export async function saveRoutine(input: RoutineInput): Promise<Routine> {
  const res = await jsonFetch<{ routine: Routine }>('/routine', {
    method: 'PUT',
    body: JSON.stringify(input),
  });
  return res.routine;
}
